import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { Box } from "@mui/material";
import { ParagraphText, UBInput } from "../../../Libraries";
import { LanguageContext } from "../../../context/LanguageContext";
import {
  InputSearchIcon,
  AddContactIcon,
  PlusBlueIcon,
} from "../../../assets/icons/icon";
import { getInitials } from "../../../utils";
import styles from "./styles.module.css";
import { sentenceCase } from "../../../constants";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import AddCustomerModal from "../Modals/AddCustomerModal";
import { phoneNumRegex } from "../../../validations";

const DROPDOWN_HEIGHT = 244;
const DROPDOWN_WIDTH = 200;
const MARGIN = 8;

const AddContactSelector = ({
  selectedContact,
  contacts = [],
  onSelectContact,
  onSelect,
  isOpen,
  onOpenChange,
  addContactLabel = "Add contact",
  searchPlaceholder = "Search contact",
  contactType = "in",
  isPhoneRequired = false,
  hideTriggerIcon = false,
}) => {
  const [I18n] = useContext(LanguageContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [createContactModal, setCreateContactModal] = useState(false);
  const [initName, setInitName] = useState("");
  const [initPhone, setInitPhone] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [openUpward, setOpenUpward] = useState(false);

  const filteredContacts =
    contacts?.length > 0 && searchQuery
      ? contacts.filter((c) =>
          c.name?.toLowerCase().includes(searchQuery?.toLowerCase()),
        )
      : contacts || [];

  const displayText = searchPlaceholder || I18n.search_contact;
  const addContactText = addContactLabel || I18n.add_contact;

  const handleToggle = (e) => {
    e?.stopPropagation?.();
    onOpenChange?.(!isOpen);
  };

  const handleSelect = (contact) => {
    triggerLogEvent(analytics, "acc_contact_selected", {
      contact_id: contact?.id,
    });
    (onSelectContact || onSelect)?.(contact);
    setSearchQuery("");
  };

  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      const inWrapper = wrapperRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inWrapper && !inDropdown) {
        onOpenChange?.(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (!isOpen || !wrapperRef.current) return;
    let scrollEl = wrapperRef.current.parentElement;
    while (scrollEl) {
      const { overflowY } = getComputedStyle(scrollEl);
      if (
        overflowY === "auto" ||
        overflowY === "scroll" ||
        overflowY === "overlay"
      ) {
        const handleScroll = () => onOpenChange?.(false);
        scrollEl.addEventListener("scroll", handleScroll, { passive: true });
        return () => scrollEl.removeEventListener("scroll", handleScroll);
      }
      scrollEl = scrollEl.parentElement;
    }
  }, [isOpen, onOpenChange]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setOpenUpward(false);
      return;
    }
    if (!wrapperRef.current) return;
    const triggerRect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const shouldOpenUpward = spaceBelow < DROPDOWN_HEIGHT + MARGIN;
    setOpenUpward(shouldOpenUpward);

    const top = shouldOpenUpward
      ? triggerRect.top - DROPDOWN_HEIGHT - MARGIN
      : triggerRect.bottom + MARGIN;
    const left = triggerRect.left;
    setDropdownPosition({ top, left });
  }, [isOpen]);

  return (
    <Box ref={wrapperRef} className={styles.wrapper}>
      <Box
        className={`${styles.trigger} ${isOpen ? styles.triggerOpen : ""} ${
          selectedContact ? styles.triggerWithName : styles.triggerAddContact
        }`}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleToggle(e);
        }}
      >
        {selectedContact ? (
          <Box className={styles.selectedContactWrapper}>
            <Box className={`${styles.contactBadge} ${styles.avatarBadge}`}>
              <span className={styles.avatarInitials}>
                {getInitials(selectedContact.name)}
              </span>
            </Box>
            <ParagraphText className={styles.contactName}>
              {sentenceCase(selectedContact.name)}
            </ParagraphText>
          </Box>
        ) : (
          <Box className={styles.selectedContactWrapper}>
            {!hideTriggerIcon && (
              <Box className={`${styles.contactBadge} ${styles.iconBadge}`}>
                <AddContactIcon className={styles.userIcon} />
              </Box>
            )}
            <ParagraphText className={hideTriggerIcon ? "newFontColor fontSize14 textDecorationUnerline" : styles.addContactText}>
              {sentenceCase(addContactText)}
            </ParagraphText>
          </Box>
        )}
      </Box>

      {isOpen &&
        ReactDOM.createPortal(
          <Box
            ref={dropdownRef}
            className={`${styles.dropdown} ${styles.dropdownPortal}`}
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
            }}
          >
            <Box className={styles.searchWrapper}>
              <UBInput
                inputId="add_contact_search"
                name="search"
                label=""
                value={searchQuery}
                placeholder={displayText}
                onChange={(_, text) => setSearchQuery(text || "")}
                onClick={(e) => e.stopPropagation()}
                inputClassname={styles.searchInput}
                baseClassname={styles.searchInputBase}
                prefix={<InputSearchIcon className={styles.searchIcon} />}
              />
            </Box>
            <Box className={styles.separator} />
            <Box className={styles.contactList}>
              {filteredContacts.length > 0 ? (
                filteredContacts.map((contact) => (
                  <Box
                    key={contact.id}
                    className={styles.contactItem}
                    onClick={() => handleSelect(contact)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        handleSelect(contact);
                    }}
                  >
                    <ParagraphText className={styles.contactItemName}>
                      {contact.name}
                    </ParagraphText>
                  </Box>
                ))
              ) : (
                <Box className={styles.emptyState}>
                  <ParagraphText className={styles.emptyText}>
                    {I18n.start_typing_to_create_one}
                  </ParagraphText>
                  <Box
                    className={styles.createContactOption}
                    onClick={() => {
                      onOpenChange?.(false);
                      if (searchQuery?.match?.(phoneNumRegex) === null) {
                        setInitName(searchQuery);
                        setInitPhone("");
                      } else {
                        setInitPhone(searchQuery);
                        setInitName("");
                      }
                      setCreateContactModal(true);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        onOpenChange?.(false);
                        if (searchQuery?.match?.(phoneNumRegex) === null) {
                          setInitName(searchQuery);
                          setInitPhone("");
                        } else {
                          setInitPhone(searchQuery);
                          setInitName("");
                        }
                        setCreateContactModal(true);
                      }
                    }}
                  >
                    <ParagraphText
                      className="fontSize12 foundationBlueNormal pointer"
                      style={{ display: "flex", alignItems: "flex-start" }}
                    >
                      <span
                        style={{
                          marginRight: 8,
                          flexShrink: 0,
                          position: "relative",
                          top: 1,
                        }}
                      >
                        <PlusBlueIcon width={12} height={12} />
                      </span>
                      <span>
                        {searchQuery
                          ? `${I18n.create || "Create"} "${searchQuery}" as ${I18n.contact?.toLowerCase() || "contact"}`
                          : `${I18n.create || "Create"} as ${I18n.contact?.toLowerCase() || "contact"}`}
                      </span>
                    </ParagraphText>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>,
          document.body,
        )}

      <Box onClick={(e) => e.stopPropagation()}>
        <AddCustomerModal
          isOpen={createContactModal}
          handleClose={() => setCreateContactModal(false)}
          initialName={initName}
          initialPhone={initPhone}
          type={contactType}
          isTypeField={false}
          isOnlyPhoneList={isPhoneRequired}
          onSaveCallback={(customer) => {
            setCreateContactModal(false);
            setSearchQuery("");
            if (customer) (onSelectContact || onSelect)?.(customer);
          }}
        />
      </Box>
    </Box>
  );
};

export default AddContactSelector;
